import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { toast } from "sonner";
import { PageHeader, Empty } from "@/components/PageHeader";
import { ShareDialog } from "@/components/ShareDialog";
import { api, apiError } from "@/lib/api";
import { openInvoicePdf } from "@/lib/download";
import { fmtDate, money, today } from "@/lib/format";
import { Plus, Trash } from "@phosphor-icons/react";

const TYPES = ["Journal", "Receipt", "Payment", "Contra", "Credit Note", "Debit Note"];
const FILTERS = ["All", "Sales", "Purchase", "Receipt", "Payment", "Journal", "Contra"];

export default function Vouchers() {
  const [rows, setRows] = useState([]);
  const [ledgers, setLedgers] = useState([]);
  const [filter, setFilter] = useState("All");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [shareOf, setShareOf] = useState(null);
  const [head, setHead] = useState({ type: "Journal", date: today(), narration: "", ref: "" });
  const [lines, setLines] = useState([{ ledger_id: "", dr: "", cr: "" }, { ledger_id: "", dr: "", cr: "" }]);

  const load = async () => {
    const params = filter === "All" ? "" : `?type=${encodeURIComponent(filter)}`;
    const [v, l] = await Promise.all([api.get(`/vouchers${params}`), api.get("/ledgers")]);
    setRows(v.data);
    setLedgers(l.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const totals = useMemo(() => ({
    dr: lines.reduce((s, l) => s + Number(l.dr || 0), 0),
    cr: lines.reduce((s, l) => s + Number(l.cr || 0), 0),
  }), [lines]);

  const save = async () => {
    try {
      await api.post("/vouchers", {
        ...head,
        entries: lines.filter((l) => l.ledger_id && (Number(l.dr) || Number(l.cr)))
          .map((l) => ({ ledger_id: l.ledger_id, dr: Number(l.dr || 0), cr: Number(l.cr || 0) })),
      });
      toast.success("Voucher posted");
      setOpen(false);
      setLines([{ ledger_id: "", dr: "", cr: "" }, { ledger_id: "", dr: "", cr: "" }]);
      setHead({ type: "Journal", date: today(), narration: "", ref: "" });
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const cancel = async (id) => {
    try { await api.post(`/vouchers/${id}/cancel`); toast.success("Voucher cancelled"); setDetail(null); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const filtered = rows.filter((r) => !q ||
    r.number.toLowerCase().includes(q.toLowerCase()) ||
    (r.party_name || "").toLowerCase().includes(q.toLowerCase()) ||
    (r.narration || "").toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="space-y-6" data-testid="vouchers-page">
      <PageHeader title="Day Book & Vouchers" subtitle="Every posted entry, hash-chained into an immutable audit trail.">
        <Input placeholder="Search number, party, narration…" value={q} onChange={(e) => setQ(e.target.value)}
          className="h-8 w-64 text-sm" data-testid="voucher-search-input" />
        <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid="new-voucher-btn">New voucher</Button>
        <Button asChild size="sm" data-testid="new-sales-invoice-btn"><Link to="/invoices/sales/new">Sales invoice</Link></Button>
      </PageHeader>

      <div className="flex flex-wrap gap-1 border-b border-border pb-2">
        {FILTERS.map((f) => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`voucher-filter-${f}`}
            className={`px-2.5 py-1 text-xs ${filter === f ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-accent"}`}
            style={{ transitionProperty: "background-color, color" }}>
            {f}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <Empty label="No vouchers" hint="Post a journal or raise an invoice to see it here." />
      ) : (
        <div className="overflow-x-auto border border-border">
          <table className="dense-table w-full min-w-[900px]" data-testid="vouchers-table">
            <thead>
              <tr><th>Date</th><th>Type</th><th>Number</th><th>Party / Narration</th>
                <th className="text-right">Amount</th><th>GST</th><th>Status</th></tr>
            </thead>
            <tbody>
              {filtered.map((v) => (
                <tr key={v.id} className="cursor-pointer row-in" onClick={() => setDetail(v)} data-testid={`voucher-row-${v.id}`}>
                  <td className="num whitespace-nowrap">{fmtDate(v.date)}</td>
                  <td>{v.type}</td>
                  <td className="num">{v.number}</td>
                  <td className="max-w-[300px] truncate">{v.party_name || v.narration || "—"}</td>
                  <td className="num text-right font-medium">{money(v.amount)}</td>
                  <td className="num text-[11px] text-muted-foreground">
                    {v.gst?.irn ? `IRN ${v.gst.irn.slice(0, 8)}…` : v.gst?.supply_type ? v.gst.supply_type.split(" ")[0] : "—"}
                  </td>
                  <td>
                    <span className={`px-1.5 py-0.5 text-[10px] uppercase tracking-widest ${
                      v.status === "Posted" ? "bg-success/10 text-success" :
                      v.status === "Cancelled" ? "bg-destructive/10 text-destructive" : "bg-muted text-muted-foreground"}`}>
                      {v.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Journal editor */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl" data-testid="voucher-editor-dialog">
          <DialogHeader><DialogTitle className="font-display">Voucher entry</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-4">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest">Type</Label>
                <Select value={head.type} onValueChange={(v) => setHead({ ...head, type: v })}>
                  <SelectTrigger data-testid="voucher-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest">Date</Label>
                <Input type="date" data-testid="voucher-date-input" value={head.date} onChange={(e) => setHead({ ...head, date: e.target.value })} />
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label className="text-xs uppercase tracking-widest">Reference</Label>
                <Input data-testid="voucher-ref-input" value={head.ref} onChange={(e) => setHead({ ...head, ref: e.target.value })} />
              </div>
            </div>

            <table className="dense-table w-full">
              <thead><tr><th>Ledger</th><th className="w-32 text-right">Debit</th><th className="w-32 text-right">Credit</th><th className="w-8" /></tr></thead>
              <tbody>
                {lines.map((l, i) => (
                  <tr key={i}>
                    <td>
                      <Select value={l.ledger_id} onValueChange={(v) => setLines(lines.map((x, j) => (j === i ? { ...x, ledger_id: v } : x)))}>
                        <SelectTrigger className="h-8 border-0 text-sm" data-testid={`entry-ledger-select-${i}`}><SelectValue placeholder="Select ledger" /></SelectTrigger>
                        <SelectContent className="max-h-72">
                          {ledgers.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </td>
                    <td>
                      <Input type="number" className="num h-8 border-0 text-right" data-testid={`entry-dr-input-${i}`} value={l.dr}
                        onChange={(e) => setLines(lines.map((x, j) => (j === i ? { ...x, dr: e.target.value, cr: "" } : x)))} />
                    </td>
                    <td>
                      <Input type="number" className="num h-8 border-0 text-right" data-testid={`entry-cr-input-${i}`} value={l.cr}
                        onChange={(e) => setLines(lines.map((x, j) => (j === i ? { ...x, cr: e.target.value, dr: "" } : x)))} />
                    </td>
                    <td className="text-right">
                      {lines.length > 2 && (
                        <button onClick={() => setLines(lines.filter((_, j) => j !== i))} data-testid={`entry-remove-${i}`}
                          className="text-muted-foreground hover:text-destructive"><Trash size={13} /></button>
                      )}
                    </td>
                  </tr>
                ))}
                <tr className="font-semibold">
                  <td>
                    <button onClick={() => setLines([...lines, { ledger_id: "", dr: "", cr: "" }])}
                      data-testid="entry-add-row" className="flex items-center gap-1 text-xs text-primary">
                      <Plus size={12} /> Add line
                    </button>
                  </td>
                  <td className="num text-right">{money(totals.dr)}</td>
                  <td className="num text-right">{money(totals.cr)}</td>
                  <td />
                </tr>
              </tbody>
            </table>

            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest">Narration</Label>
              <Input data-testid="voucher-narration-input" value={head.narration} onChange={(e) => setHead({ ...head, narration: e.target.value })} />
            </div>
            <p className={`text-xs ${Math.abs(totals.dr - totals.cr) < 0.01 && totals.dr > 0 ? "text-success" : "text-destructive"}`} data-testid="voucher-balance-status">
              {Math.abs(totals.dr - totals.cr) < 0.01 && totals.dr > 0
                ? "Balanced — ready to post"
                : `Difference ${money(Math.abs(totals.dr - totals.cr))} — debits must equal credits`}
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="voucher-cancel-btn">Cancel</Button>
            <Button onClick={save} disabled={!(Math.abs(totals.dr - totals.cr) < 0.01 && totals.dr > 0)} data-testid="voucher-post-btn">
              Post voucher
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail sheet */}
      <Sheet open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-xl" data-testid="voucher-detail-sheet">
          <SheetHeader><SheetTitle className="font-display">{detail?.type} · {detail?.number}</SheetTitle></SheetHeader>
          {detail ? (
            <div className="mt-4 space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-px border border-border bg-border">
                {[["Date", fmtDate(detail.date)], ["Amount", money(detail.amount)],
                  ["Party", detail.party_name || "—"], ["Status", detail.status]].map(([k, v]) => (
                  <div key={k} className="bg-background p-3">
                    <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{k}</p>
                    <p className="num">{v}</p>
                  </div>
                ))}
              </div>
              {detail.gst?.irn ? (
                <div className="border border-border p-3">
                  <p className="text-[10px] uppercase tracking-widest text-muted-foreground">e-Invoice (simulated NIC)</p>
                  <p className="num break-all text-xs">IRN {detail.gst.irn}</p>
                  <p className="num text-xs text-muted-foreground">Ack {detail.gst.ack_no} · {detail.gst.ack_date}</p>
                </div>
              ) : null}
              <table className="dense-table w-full">
                <thead><tr><th>Ledger</th><th className="text-right">Debit</th><th className="text-right">Credit</th></tr></thead>
                <tbody>
                  {(detail.entries || []).map((e, i) => (
                    <tr key={i}>
                      <td>{e.ledger_name}</td>
                      <td className="num text-right">{e.dr ? money(e.dr) : "—"}</td>
                      <td className="num text-right">{e.cr ? money(e.cr) : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {detail.narration ? <p className="text-xs text-muted-foreground">{detail.narration}</p> : null}
              <div className="flex flex-wrap gap-2">
                {["Sales", "Purchase", "Credit Note", "Debit Note"].includes(detail.type) && (
                  <>
                    <Button size="sm" data-testid="voucher-share-btn" onClick={() => setShareOf(detail)}>Share</Button>
                    <Button variant="outline" size="sm" data-testid="voucher-pdf-btn"
                      onClick={async () => {
                        try { await openInvoicePdf(detail.id, detail.number, "download"); }
                        catch (e) { toast.error(e.message); }
                      }}>Download PDF</Button>
                    <Button variant="outline" size="sm" data-testid="voucher-print-btn"
                      onClick={async () => {
                        try { await openInvoicePdf(detail.id, detail.number, "print"); }
                        catch (e) { toast.error(e.message); }
                      }}>Print</Button>
                  </>
                )}
                {detail.status === "Posted" && (
                  <Button variant="outline" size="sm" onClick={() => cancel(detail.id)} data-testid="voucher-cancel-action"
                    className="text-destructive">Cancel voucher</Button>
                )}
              </div>
            </div>
          ) : null}
        </SheetContent>
      </Sheet>

      {shareOf ? <ShareDialog voucher={shareOf} open={!!shareOf} onOpenChange={(o) => !o && setShareOf(null)} /> : null}
    </div>
  );
}
